import React, { useEffect, useState } from 'react';
import Select from 'react-select';
import { supabase } from '../lib/supabaseClient';
import { MathJaxContext } from 'better-react-mathjax';
import Swal from 'sweetalert2';
import { renderBlocks }  from '../components/renderBlocks'; // Ensure this is your universal renderer
import { MathJax } from 'better-react-mathjax';
const mathJaxConfig = {
  loader: { load: ['input/tex', 'output/chtml'] },
  tex: {
    inlineMath: [['$', '$'], ['\\(', '\\)']],
    displayMath: [['\\[', '\\]']],
  },
};



export default function InsertQuiz() {
  const [jsonText, setJsonText] = useState('');
  const [parsedQuestions, setParsedQuestions] = useState([]);
  const [outlineOptions, setOutlineOptions] = useState([]);
  const [selectedOutlineId, setSelectedOutlineId] = useState('');
  const [status, setStatus] = useState('');
  const [previewConfirmed, setPreviewConfirmed] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    const fetchOutlines = async () => {
      const { data, error } = await supabase
        .from('topics')
        .select('id, name, parent:parent_id ( module_code )')
        .eq('level', 3)
        .order('name', { ascending: true });

      if (!error && data) {
        const formatted = data.map((o) => ({
          value: o.id,
          label: `${o.parent?.module_code || '❓'} - ${o.name}`,
        }));
        setOutlineOptions(formatted);
      }
    };

    fetchOutlines();
  }, []);

  const handleParse = () => {
    try {
      const parsed = JSON.parse(jsonText);
      if (!Array.isArray(parsed)) throw new Error('Expected a JSON array');
      setParsedQuestions(parsed);
      setStatus(`✅ Validated ${parsed.length} questions.`);
      setPreviewConfirmed(false);
    } catch (err) {
      setParsedQuestions([]);
      setStatus('❌ Invalid JSON format.');
      setPreviewConfirmed(false);
    }
  };

  const handleConfirmPreview = () => {
    if (!selectedOutlineId || parsedQuestions.length === 0) {
      setStatus('❌ Select an outline and paste valid JSON.');
      return;
    }
    setPreviewConfirmed(true);
    setStatus('✅ Preview confirmed. You can now insert.');
  };

  const handleInsert = async () => {
    setIsLoading(true);
    const inserts = parsedQuestions.map((q) => ({
      outline_id: selectedOutlineId,
      question_blocks: q.question_blocks,
      options: q.options,
      explanation_blocks: q.explanation_blocks,
      image_url: q.image_url || null,
    }));

    const { error } = await supabase.from('quiz_questions').insert(inserts);

    setIsLoading(false);
    if (error) {
      console.error('Insert error:', error);
      setStatus('❌ Error inserting questions.');
    } else {
      setStatus(`✅ Successfully inserted ${inserts.length} questions!`);
      setJsonText('');
      setParsedQuestions([]);
      setPreviewConfirmed(false);
    }
  };

  const handleFetch = async () => {
    if (!selectedOutlineId) {
      setStatus('❌ Please select an outline to fetch.');
      return;
    }

    const { data, error } = await supabase
      .from('quiz_questions')
      .select('*')
      .eq('outline_id', selectedOutlineId);

    if (error || !data) {
      setStatus('❌ Error fetching existing questions.');
    } else {
      setJsonText(JSON.stringify(data, null, 2));
      setParsedQuestions(data);
      setStatus(`✅ Loaded ${data.length} questions.`);
      setPreviewConfirmed(true);
    }
  };

  const handleDelete = async () => {
    if (!selectedOutlineId) {
      setStatus('❌ Select an outline to delete questions.');
      return;
    }

    const result = await Swal.fire({
      title: 'Are you sure?',
      text: 'This will delete all quiz questions for the selected outline!',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Yes, delete',
      cancelButtonText: 'Cancel',
    });

    if (!result.isConfirmed) return;

    const { error } = await supabase
      .from('quiz_questions')
      .delete()
      .eq('outline_id', selectedOutlineId);

    if (error) {
      setStatus('❌ Error deleting questions.');
    } else {
      setStatus('✅ All questions deleted for selected outline.');
      setJsonText('');
      setParsedQuestions([]);
      setPreviewConfirmed(false);
    }
  };

  const handleUpdate = async () => {
    if (!selectedOutlineId || parsedQuestions.length === 0) {
      setStatus('❌ Select outline and provide valid JSON.');
      return;
    }

    const result = await Swal.fire({
      title: 'Confirm Overwrite',
      text: 'This will replace all existing questions for this outline. Continue?',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Yes, overwrite',
      cancelButtonText: 'Cancel',
    });

    if (!result.isConfirmed) return;

    setIsLoading(true);

    const { error: deleteError } = await supabase
      .from('quiz_questions')
      .delete()
      .eq('outline_id', selectedOutlineId);

    if (deleteError) {
      setIsLoading(false);
      setStatus('❌ Failed to delete existing questions.');
      return;
    }

    const inserts = parsedQuestions.map((q) => ({
      outline_id: selectedOutlineId,
      question_blocks: q.question_blocks,
      options: q.options,
      explanation_blocks: q.explanation_blocks,
      image_url: q.image_url || null,
    }));

    const { error: insertError } = await supabase
      .from('quiz_questions')
      .insert(inserts);

    setIsLoading(false);

    if (insertError) {
      setStatus('❌ Failed to insert updated questions.');
    } else {
      setStatus(`✅ Updated ${inserts.length} questions successfully.`);
    }
  };

  return (
    <MathJaxContext config={mathJaxConfig}>
      <div className="max-w-5xl mx-auto px-4 py-8">
        <h2 className="text-2xl font-bold mb-6 text-center text-indigo-700">🛠 Insert Quiz Questions</h2>

        <div className="mb-4">
          <label className="block font-medium text-gray-700 mb-2">Select Outline:</label>
          <Select
            options={outlineOptions}
            onChange={(selected) => setSelectedOutlineId(selected?.value || '')}
            placeholder="Search and select an outline..."
          />
        </div>

        <div className="mb-4">
          <label className="block font-medium text-gray-700 mb-1">Paste JSON Array of Questions:</label>
          <textarea
            rows="10"
            className="w-full border border-gray-300 rounded px-3 py-2 font-mono text-sm"
            value={jsonText}
            onChange={(e) => setJsonText(e.target.value)}
            placeholder='[ { "question_blocks": [...], "options": [...], "explanation_blocks": [...], "image_url": null } ]'
          />
          <div className="mt-2 flex flex-wrap gap-3">
            <button onClick={handleParse} className="px-4 py-1 bg-blue-600 text-white rounded hover:bg-blue-700">Validate JSON</button>
            <button onClick={handleFetch} className="px-4 py-1 bg-purple-600 text-white rounded hover:bg-purple-700">Fetch</button>
            <button onClick={handleUpdate} className="px-4 py-1 bg-orange-600 text-white rounded hover:bg-orange-700">Replace</button>
            <button onClick={handleDelete} className="px-4 py-1 bg-red-600 text-white rounded hover:bg-red-700">Delete</button>
            {parsedQuestions.length > 0 && (
              <button onClick={handleConfirmPreview} className="px-4 py-1 bg-yellow-500 text-white rounded hover:bg-yellow-600">Confirm Preview</button>
            )}
          </div>
        </div>

        {isLoading && (
          <div className="text-indigo-600 mb-3 text-sm font-medium">⏳ Processing...</div>
        )}

        {status && (
          <div className="mb-4 text-sm font-medium text-indigo-700">{status}</div>
        )}

        {previewConfirmed && (
          <button
            onClick={handleInsert}
            className="px-6 py-2 bg-green-600 text-white rounded hover:bg-green-700 transition mb-6"
          >
            🚀 Insert {parsedQuestions.length} Questions
          </button>
        )}

        {parsedQuestions.length > 0 && (
          <div className="mt-6">
            <h3 className="text-lg font-semibold mb-4 text-gray-800">🧪 Preview Questions</h3>
            <ul className="space-y-6">
              {parsedQuestions.map((q, i) => (
                <li key={i} className="border border-gray-200 rounded p-4 bg-white">
                  <div className="mb-2 text-gray-800 text-sm sm:text-base grid grid-cols-[auto,1fr] gap-2 items-start">
                    <span className="font-semibold text-indigo-700">Q{i + 1}:</span>
                    {renderBlocks([q.question_blocks[0]])}

                    {q.question_blocks
                      .slice(1)
                      .map((block, idx) => (
                        <div key={idx} className="col-span-2 mt-2">
                          {renderBlocks([block])}
                        </div>
                      ))}
                  </div>

                  <ul className="ml-4 list-disc text-gray-700 text-sm">
                    {q.options.map((opt, idx) => (
                      <li key={idx} className={opt.is_correct ? 'text-green-700 font-semibold' : ''}>
                        {renderBlocks(opt.blocks)}
                      </li>
                    ))}
                  </ul>
                  {q.explanation_blocks && (
                    <div className="mt-2 text-sm text-gray-600">
                      <strong>Explanation:</strong>
                      {renderBlocks(q.explanation_blocks)}
                    </div>
                  )}
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
    </MathJaxContext>
  );
}
